import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { UnstyledLink } from '@zocdoc-mezzanine/components-link';
import { blue60 } from '@zocdoc-mezzanine/foundations-colors';
import {
    labelSmall,
    labelMedium,
    labelLarge,
} from '@zocdoc-mezzanine/foundations-typography';
import handlePropValidation from './utils/usePropValidation';

/**
 * BaseButton
 * This component should only be used internally. It contains minimal styling. All functionality belongs here.
 * Button Variants should import this component and apply its own styling on top of it. The API should remain the exact same.
 * @param {ButtonProps} props - Props for the button.
 * @param {string} className - enables variants to wrap BaseButton with styled-components
 * @returns {JSX.Element} BaseButton
 */

/**
 * @enum {Button.Size}
 */
const Size = {
    Large: 'large',
    Default: 'default',
};

const ButtonIconMargin = '8px';

//filter out disabled prop since it's not needed for <a/>
function LinkButton({ disabled, children, ...rest }) {
    if (disabled) {
        return (
            <StyledLink {...rest} tabIndex={-1}>
                {children}
            </StyledLink>
        );
    }

    return <StyledLink {...rest}> {children} </StyledLink>;
}

const BaseButton = forwardRef(
    (
        {
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledby,
            'aria-describedby': ariaDescribedby,
            'data-test': dataTest,
            fluid = false,
            size = 'default',
            disabled = false,
            className,
            onClick,
            href,
            children,
            type,
            target,
            rel,
            title,
            beforeClick,
        },
        ref
    ) => {
        handlePropValidation({ onClick, beforeClick, href, type });

        const commonProps = {
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledby,
            'aria-describedby': ariaDescribedby,
            'data-test': dataTest,
            className,
            disabled,
            $fluid: fluid,
            $size: size,
        };

        if (href) {
            return (
                <LinkButton
                    {...commonProps}
                    href={href}
                    ref={ref}
                    target={target}
                    rel={rel}
                    title={title}
                    beforeClick={beforeClick}
                >
                    {children}
                </LinkButton>
            );
        }

        return (
            <Button {...commonProps} onClick={onClick} ref={ref} type={type}>
                {children}
            </Button>
        );
    }
);

const buttonSize = ({ $size }) => {
    switch ($size) {
        case Size.Large:
            return css`
                ${labelMedium}
                ${breakpoint.small} {
                    ${labelLarge}
                }
            `;

        case Size.Default:
        default:
            return css`
                ${labelSmall}
            `;
    }
};

const baseButtonStyles = css`
    text-align: center;
    padding: 12px 24px;
    border-radius: 2px;
    box-sizing: border-box;
    border: none;
    position: relative;
    cursor: pointer;

    //IE doesn't support outline-offset, so below block is needed to accommodate all browsers
    &:focus::after {
        content: '';
        position: absolute;
        height: calc(100% + 4px);
        width: calc(100% + 4px);
        left: -2px;
        top: -2px;
        background-color: transparent;
        border-radius: 2px;
        box-shadow: 0px 0px 0px 2px ${blue60};
    }

    ${({ $fluid }) => $fluid && `width: 100%;`}
    ${buttonSize}
`;

const Button = styled.button`
    ${baseButtonStyles}
`;

const StyledLink = styled(UnstyledLink)`
    display: inline-block;
    ${baseButtonStyles}
`;

export { BaseButton as default, Size, ButtonIconMargin };
